import React, {useState, useRef, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {useAIChatStream, type Message} from '../ai-chat/useAIChatStream';
import {EmptyState} from '../ai-chat/EmptyState';
import {MessageBubble} from '../ai-chat/MessageBubble';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {canOpenReportBuilder} from '../ai-report-builder/reportBuilderGate';
import type {ReportIntentPayload} from '../ai-report-builder/ReportBuilderContext';
import {parseReportTaxonomy, type ReportTaxonomy} from '../ai-report-builder/contracts/report-taxonomy.types';
import {D} from '../ai-chat/design-tokens';
import DrawerFooter from './DrawerFooter';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';
import CreateSavedPromptMutation from '../../mutations/agent/CreateSavedPromptMutation';
import {CommitMutation} from '../../mutations/CommitMutation';
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';
import {getProjectIdFromPath} from '../../forecast-app/shared/util/UrlContextUtil';
import {useForecastFetchQuery} from '../../forecast-app/shared/hooks/useForecastFetchQuery';
import type {ChatPanelReportTaxonomyQuery$data} from './__generated__/ChatPanelReportTaxonomyQuery.graphql';

// v1 report builder supports one report type only; the enum name the backend taxonomy resolver
// expects (see schema.graphql `Viewer.reportTaxonomy(reportType:)`).
const TASK_REPORT_TYPE = 'TASK_REPORT';

const chatPanelReportTaxonomyQuery = graphql`
	query ChatPanelReportTaxonomyQuery($reportType: String) {
		viewer {
			reportTaxonomy(reportType: $reportType)
		}
	}
`;

const MessagesArea = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 20px;
	background: ${D.bg};
	position: relative;
`;

const ScrollToBottomBtn = styled.button`
	position: absolute;
	bottom: 12px;
	left: 50%;
	transform: translateX(-50%);
	background: ${D.bg};
	border: 1px solid ${D.border};
	border-radius: 9999px;
	padding: 4px 12px;
	font-size: 11px;
	color: ${D.textMuted};
	cursor: pointer;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
	display: flex;
	align-items: center;
	gap: 4px;

	&:hover {
		background: ${D.surface};
	}
`;

const PanelWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
`;

// The create-project patch is untyped JSON from the agent, so every field can arrive as something
// other than a string. Forward only strings: the modal parses these (lower-casing the budget type,
// handing the dates to moment) and a stray number there throws while the modal is mounting.
const asString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

type ChatPanelProps = {
	viewerPersonId?: number;
	companyId?: string;
	companyName?: string;
	history: {push: (path: string, state?: unknown) => void} | null | undefined;
	onRegisterClear?: (clearFn: () => void) => void;
	onRegisterRegenerate?: (regenerateFn: () => void) => void;
	onHasLlmResponseChange?: (hasResponse: boolean) => void;
};

const ChatPanel = ({
	viewerPersonId,
	companyId,
	companyName,
	history,
	onRegisterClear,
	onRegisterRegenerate,
	onHasLlmResponseChange,
}: ChatPanelProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {inputValue, setInputValue, savedPromptTexts, setSavedPromptTexts, registerReportIntentSavedRecorder} =
		useAgentDrawerContext();
	const [input, setInput] = useState('');
	const [showScrollBtn, setShowScrollBtn] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (inputValue) {
			setInput(inputValue);
			setInputValue('');
			inputRef.current?.focus();
		}
	}, [inputValue, setInputValue]);

	const handleProjectIntent = useCallback((data: Record<string, unknown>) => {
		const patch = (data?.patch ?? {}) as Record<string, unknown>;
		const noRateCard = patch.noRateCard;
		showModal({
			type: MODAL_TYPE.NEW_PROJECT_V2,
			initialName: asString(patch.name),
			initialStartDate: asString(patch.startDate),
			initialEndDate: asString(patch.endDate),
			initialBudgetType: asString(patch.budgetType),
			// The rate card arrives as the name the user said, not an id - the modal matches it
			// against the company's cards. Without this the modal always opens on the company
			// default, so a named card silently turned into the wrong one. The "no rate card"
			// flag is accepted as either a boolean or its string form.
			initialRateCardName: asString(patch.rateCard),
			initialNoRateCard: noRateCard === true || noRateCard === 'true',
		});
	}, []);

	// `setMessages` only exists once the hook below runs; hold it in a ref (populated by an effect
	// after the hook) so this callback — which is passed INTO the hook — can push a card.
	const pushIntentMessageRef = useRef<((updater: (prev: Message[]) => Message[]) => void) | null>(null);

	// Taxonomy for the current report intent — fetched lazily (not blocking the card render) so the
	// condition summary can upgrade from the agent's own `summaries[].text` to a taxonomy-driven
	// render once the fetch resolves. A single value (not per-message) is sufficient: only one
	// report intent is ever 'pending' at a time (see the supersede filter below), and confirmed/
	// cancelled cards are historical records that don't need a fresher taxonomy.
	const [reportTaxonomy, setReportTaxonomy] = useState<ReportTaxonomy | undefined>(undefined);
	const {data: reportTaxonomyQueryData, fetch: fetchReportTaxonomy} = useForecastFetchQuery<
		ChatPanelReportTaxonomyQuery$data,
		{reportType: string}
	>(chatPanelReportTaxonomyQuery);

	// The hook's `fetch` takes an onSuccess callback but does not hand it the resolved data (it only
	// signals completion) — react to the returned `data` instead once Relay resolves it. Parse
	// defensively: a malformed/empty JSON string must not crash the chat surface, it should just
	// leave the card on its non-taxonomy fallback (summaries/prompt).
	useEffect(() => {
		const raw = reportTaxonomyQueryData?.viewer?.reportTaxonomy;
		if (!raw) return;
		try {
			const parsed = parseReportTaxonomy(raw);
			if (parsed.fields && parsed.fields.length > 0) {
				setReportTaxonomy(parsed);
			}
		} catch {
			// leave reportTaxonomy as-is — card falls back to summaries/prompt
		}
	}, [reportTaxonomyQueryData]);

	const handleReportIntent = useCallback(
		(data: Record<string, unknown>) => {
			if (!canOpenReportBuilder()) return;
			// A create_report_intent tool result whose content is the JSON literal `null` (or any
			// non-object) parses without throwing; guard so no malformed card is surfaced.
			if (!data || typeof data !== 'object') return;
			// Do NOT open the builder directly. Surface a HITL confirm card in the chat (mirroring
			// write-action confirmations); the builder opens only once the user confirms and clicks View
			// — see MessageBubble → ReportIntentCard and the onViewReportIntent handler below.
			const id = `report-intent-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
			pushIntentMessageRef.current?.(prev => [
				// Supersede any still-pending report proposal: a refined intent replaces the prior
				// unactioned card instead of stacking a second one. Confirmed/cancelled cards are
				// terminal records of a decision the user already made, so they are kept as history.
				...prev.filter(m => !(m.reportIntent && m.reportIntent.status === 'pending')),
				{id, role: 'assistant', content: '', reportIntent: {payload: data, status: 'pending'}},
			]);
			// Fetch the taxonomy alongside the card, not before it — the card renders immediately from
			// `summaries`/`prompt` (see ReportIntentCard) and upgrades in place once (if) the fetch
			// above resolves and the effect parses it.
			fetchReportTaxonomy({reportType: TASK_REPORT_TYPE});
		},
		[fetchReportTaxonomy]
	);

	const handleProjectDeleted = useCallback(
		(_relayNodeId: string, customProjectId?: string) => {
			if (!history) return;
			if (!window.location.pathname.startsWith('/project/')) return;
			// Only navigate away if we're viewing the specific project that was deleted.
			// getProjectIdFromPath extracts the customProjectId slug from the URL (e.g. "/project/P-42/..." → "P-42").
			// If the slug matches the deleted project's customProjectId, navigate to the list.
			// If slug extraction fails (URL without sub-path) or customProjectId is unknown, navigate anyway
			// — the deleted project is gone and any /project/* page would be broken.
			if (customProjectId) {
				const currentSlug = getProjectIdFromPath(window.location.pathname);
				if (currentSlug !== null && currentSlug !== customProjectId) return;
			}
			history.push('/projects');
		},
		[history]
	);

	const {messages, setMessages, isStreaming, isHitlPending, sendMessage, respondToAction, abort, clearMessages, resetThread} =
		useAIChatStream({
			viewerPersonId,
			companyId,
			companyName,
			onProjectIntent: handleProjectIntent,
			onReportIntent: handleReportIntent,
			onProjectDeleted: handleProjectDeleted,
		});

	const hasLlmResponse = messages.some(m => m.role === 'assistant');

	// Abort any in-flight agent run when the panel unmounts (e.g. after navigation triggered by handleProjectDeleted),
	// preventing state updates on an unmounted component from the still-running SSE stream.
	const abortRef = useRef(abort);
	useEffect(() => {
		abortRef.current = abort;
	}, [abort]);
	useEffect(() => () => abortRef.current(), []);

	useEffect(() => {
		onHasLlmResponseChange?.(hasLlmResponse);
	}, [hasLlmResponse, onHasLlmResponseChange]);

	useEffect(() => {
		onRegisterClear?.(() => {
			clearMessages();
			setInput('');
			setShowScrollBtn(false);
		});
	}, [clearMessages, onRegisterClear]);

	// Keep refs so the registered regenerate fn always sees current values without re-registering
	const messagesRef = useRef(messages);
	const isStreamingRef = useRef(isStreaming);
	const sendMessageRef = useRef(sendMessage);
	const setMessagesRef = useRef(setMessages);
	const resetThreadRef = useRef(resetThread);
	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);
	useEffect(() => {
		isStreamingRef.current = isStreaming;
	}, [isStreaming]);
	useEffect(() => {
		sendMessageRef.current = sendMessage;
	}, [sendMessage]);
	useEffect(() => {
		setMessagesRef.current = setMessages;
	}, [setMessages]);
	useEffect(() => {
		pushIntentMessageRef.current = setMessages;
	}, [setMessages]);
	useEffect(() => {
		resetThreadRef.current = resetThread;
	}, [resetThread]);

	// Resend a user query, replaying the conversation up to that point.
	// - The drawer header button calls this with no argument → resend the LAST
	//   user query.
	// - An expired action card calls this with its own actionId → resend the user
	//   query that produced THAT card, so a mid-transcript card regenerates the
	//   right prompt (not whatever the latest turn happened to be).
	// The thread is rotated first so the agent starts from a clean checkpoint —
	// otherwise a stale pending/expired action still in the graph state makes the
	// agent defer to the old card instead of producing a fresh plan.
	const regenerate = useCallback((actionId?: string) => {
		if (isStreamingRef.current) return;
		const msgs = messagesRef.current;
		let userIdx = -1;
		if (actionId) {
			// Find the card, then the nearest preceding user message.
			const cardIdx = msgs.findIndex(m => m.actionPlan?.action_id === actionId);
			if (cardIdx === -1) return;
			for (let i = cardIdx; i >= 0; i--) {
				if (msgs[i].role === 'user') {
					userIdx = i;
					break;
				}
			}
		} else {
			const lastUserRev = [...msgs].reverse().findIndex(m => m.role === 'user');
			if (lastUserRev !== -1) userIdx = msgs.length - 1 - lastUserRev;
		}
		if (userIdx === -1) return;
		const history = msgs.slice(0, userIdx);
		const userText = msgs[userIdx].content;
		setMessagesRef.current(history);
		resetThreadRef.current();
		void sendMessageRef.current(userText, history);
	}, []);

	useEffect(() => {
		onRegisterRegenerate?.(regenerate);
	}, [onRegisterRegenerate, regenerate]);

	// Scroll handling
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const onScroll = () => {
			setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
		};
		el.addEventListener('scroll', onScroll);
		return () => el.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		if (!showScrollBtn) {
			messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
		}
	}, [messages, showScrollBtn]);

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
		setShowScrollBtn(false);
	}, []);

	const handleSend = useCallback(() => {
		if (isStreaming) return;
		const text = input.trim();
		setInput('');
		void sendMessage(text, messages); // empty string intentionally allowed — product decision
		setTimeout(() => inputRef.current?.focus(), 0);
	}, [input, isStreaming, sendMessage, messages]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend]
	);

	const handleSavePrompt = useCallback(
		(promptText: string) => {
			const trimmed = promptText.trim();
			if (!trimmed) return;
			const name = trimmed.length > 50 ? trimmed.substring(0, 50) : trimmed;
			CommitMutation(CreateSavedPromptMutation, {name, promptText: trimmed}, () => {
				setSavedPromptTexts(new Set([...savedPromptTexts, trimmed]));
				createToast({duration: 3000, message: formatMessage({id: 'nova.prompt.saved_toast'})});
			});
		},
		[formatMessage, savedPromptTexts, setSavedPromptTexts]
	);

	// Stamps the stored report's id onto the chat message that proposed it, so its View button stops
	// rebuilding a draft from the agent's original payload. Only the card's navigation changes — the
	// message's rendered content is a record of what the agent produced and stays as it was.
	const handleIntentSaved = useCallback(
		(messageId: string, savedReportId: string) => {
			setMessages(prev =>
				prev.map(m =>
					m.id === messageId && m.reportIntent ? {...m, reportIntent: {...m.reportIntent, savedReportId}} : m
				)
			);
		},
		[setMessages]
	);

	useEffect(
		() => registerReportIntentSavedRecorder(handleIntentSaved),
		[registerReportIntentSavedRecorder, handleIntentSaved]
	);

	return (
		<PanelWrapper>
			<MessagesArea ref={scrollRef}>
				{messages.length === 0 && <EmptyState onSuggestionClick={setInput} />}

				{messages.map((msg, idx) => (
					<MessageBubble
						key={msg.id}
						message={msg}
						isLastAssistant={msg.role === 'assistant' && idx === messages.length - 1 && isStreaming}
						history={history}
						reportTaxonomy={reportTaxonomy}
						onRespondAction={respondToAction}
						onRegenerateAction={regenerate}
						onResumeAction={actionId =>
							setMessages(prev =>
								prev.map(m => (m.actionPlan?.action_id === actionId ? {...m, actionPaused: false} : m))
							)
						}
						onConfirmReportIntent={messageId =>
							setMessages(prev =>
								prev.map(m =>
									m.id === messageId && m.reportIntent
										? {...m, reportIntent: {...m.reportIntent, status: 'confirmed'}}
										: m
								)
							)
						}
						onCancelReportIntent={messageId =>
							setMessages(prev =>
								prev.map(m =>
									m.id === messageId && m.reportIntent
										? {...m, reportIntent: {...m.reportIntent, status: 'cancelled'}}
										: m
								)
							)
						}
						onViewReportIntent={(payload, messageId, savedReportId) => {
							if (!canOpenReportBuilder()) return;
							// Once the intent has become a real report, always open THAT. The payload is a
							// snapshot of what the agent proposed and drifts the moment the report is edited,
							// so rebuilding a draft from it here would quietly discard the saved report.
							if (savedReportId) {
								history?.push(`/report-builder/${savedReportId}`);
								return;
							}
							// Never saved: the payload is the only thing that can open the builder at all. The
							// intent is ephemeral, so it rides along in location state rather than the URL (the
							// page redirects home if absent). The message id rides with it so the builder can
							// report the new report's id back to this card on its first save.
							history?.push('/report-builder', {
								initialConfig: payload as unknown as ReportIntentPayload,
								userId: viewerPersonId != null ? String(viewerPersonId) : undefined,
								taxonomy: reportTaxonomy,
								intentMessageId: messageId,
							});
						}}
					/>
				))}
				<div ref={messagesEndRef} />

				{showScrollBtn && (
					<ScrollToBottomBtn type="button" onClick={scrollToBottom}>
						↓
					</ScrollToBottomBtn>
				)}
			</MessagesArea>

			<DrawerFooter
				input={input}
				isStreaming={isStreaming}
				isHitlPending={isHitlPending}
				onInputChange={setInput}
				onSend={handleSend}
				onStop={abort}
				onKeyDown={handleKeyDown}
				inputRef={inputRef}
				onSavePrompt={handleSavePrompt}
			/>
		</PanelWrapper>
	);
};

export default ChatPanel;
